// capabilities.js — single source of truth for what each role can do.
//
// The app code never asks "is this user an admin?" anymore. It asks
// "can this user do X?" via the `useCan()` hook in AuthContext. That
// hook reads this file plus the user's `permission_overrides` array.
//
// Adding a new gated feature is two steps:
//   1. Add a capability name to CAPABILITIES below (with a human-friendly
//      label + description for the Users & Roles UI).
//   2. Add it to whichever roles should have it by default in
//      ROLE_CAPABILITIES below.
// Then anywhere in the app you can write:  if (can('your_capability')) { ... }
//
// Mental model:
//   • office     — frontline office staff (default for new invites)
//   • admin      — office lead, manages day-to-day operations
//   • super_admin— financial visibility + manages users
//   • owner      — hidden role, total control, currently just Jaylen
//
// v1 is ADDITIVE ONLY. `permission_overrides` can GRANT a capability the
// user wouldn't otherwise have via their role. It cannot revoke one.

// ---- Capability catalog ----------------------------------------------------
// Order here is the order they appear in the Users & Roles override section.
// Grouped by area to make scanning easier in the UI.
export const CAPABILITIES = [
    // Clients
    { name: 'view_clients',          group: 'Clients',    label: 'View clients',           description: 'See the clients page and individual client records.' },
    { name: 'edit_clients',          group: 'Clients',    label: 'Edit clients',           description: 'Create new clients, run intake, update client details.' },

    // Estimates
    { name: 'view_estimates',        group: 'Estimates',  label: 'View estimates',         description: 'See estimate records (without dollar amounts unless View pricing is also granted).' },
    { name: 'edit_estimates',        group: 'Estimates',  label: 'Edit estimates',         description: 'Build and modify estimates (non-price fields — addresses, notes, crew sizes, etc.).' },
    { name: 'view_estimate_pricing', group: 'Estimates',  label: 'View pricing',           description: 'See dollar amounts and pricing breakdown on individual estimates.' },
    { name: 'edit_pricing',          group: 'Estimates',  label: 'Edit pricing',           description: 'Change dollar amounts, hourly rates, and totals on estimates.' },
    { name: 'assign_estimates',      group: 'Estimates',  label: 'Assign estimates',       description: 'Set or change which user an estimate is assigned to. Unassigned estimates are visible to assignees only.' },

    // Dispatch + Crew
    { name: 'view_dispatch',         group: 'Operations', label: 'View dispatch board',    description: 'See the dispatch board and job assignments.' },
    { name: 'manage_dispatch',       group: 'Operations', label: 'Manage dispatch',        description: 'Assign crew + trucks, change job statuses, dispatch jobs.' },
    { name: 'manage_crew',           group: 'Operations', label: 'Manage crew',            description: 'Add/edit crew members, set pay rates, manage roster.' },
    { name: 'manage_crew_logins',    group: 'Operations', label: 'Manage crew logins',     description: 'Send crew portal invites (SMS setup links) and manage crew portal access.' },

    // Records + Invoicing
    { name: 'view_records',          group: 'Records',    label: 'View records',           description: 'Access the Records page (completed jobs + history).' },
    { name: 'send_invoices',         group: 'Records',    label: 'Send invoices',          description: 'Generate and send client invoices, mark paid.' },
    { name: 'delete_records',        group: 'Records',    label: 'Delete records',         description: 'Hard-delete estimates, jobs, and clients. Destructive — handle with care.' },

    // System / Admin
    { name: 'manage_settings',       group: 'System',     label: 'Manage settings',        description: 'Access the Settings page and modify statuses, priorities, etc.' },
    { name: 'manage_templates',      group: 'System',     label: 'Manage templates',       description: 'Edit document templates (contracts, invoices, BOLs).' },
    { name: 'manage_users',          group: 'System',     label: 'Manage users + roles',   description: 'Invite new users, change roles, manage permission overrides.' },
    { name: 'view_financials',       group: 'System',     label: 'View financials',        description: 'See revenue dashboards, profit margins, and money totals.' },
    { name: 'transfer_deposits',     group: 'System',     label: 'Transfer deposits',      description: 'Move a paid deposit + signed terms from one of a client’s estimates to another (e.g. after an address change).' },

    // Portal access
    { name: 'use_walkthrough_portal', group: 'Portals',   label: 'Use walkthrough portal', description: 'Access the on-site walkthrough estimator portal.' },
];

// Just the capability names as a Set — handy for quick lookups elsewhere.
export const CAPABILITY_NAMES = new Set(CAPABILITIES.map(c => c.name));

// Owner role's special "all capabilities" marker. The useCan hook treats this
// as a wildcard so future capabilities are automatically granted to owners
// without anyone having to remember to update this file.
const ALL = Symbol.for('bbm.capabilities.ALL');

// ---- Role → default capabilities -----------------------------------------
// What a brand-new user of each role can do, before any overrides are applied.
// When tuning these defaults, the rule of thumb is: prefer to err on the side
// of LESS by default. Easier to grant an extra capability via overrides than
// to discover that an Office user has been seeing financials for six months.
export const ROLE_CAPABILITIES = {
    office: [
        'view_clients',
        'edit_clients',
        'view_estimates',
        'edit_estimates',
        // Office can SEE pricing on estimates they can access (hard-hide RLS
        // limits that to estimates assigned to or created by them — see
        // sql/051).
        'view_estimate_pricing',
        // Office BUILDS estimates too (2026-07-01, Jaylen): without edit_pricing
        // the New Estimate tab renders blank because the whole pricing builder
        // (ClientPricingTab) is gated on it. Granting it lets office staff create
        // and price estimates; hard-hide RLS still scopes them to their own.
        'edit_pricing',
        'view_dispatch',
        'view_records',
    ],
    admin: [
        // Everything Office has, plus operational + estimate pricing power.
        'view_clients', 'edit_clients',
        'view_estimates', 'edit_estimates', 'view_estimate_pricing', 'edit_pricing', 'assign_estimates',
        'view_dispatch', 'manage_dispatch', 'manage_crew', 'manage_crew_logins',
        'view_records', 'send_invoices',
        'manage_settings',
        'use_walkthrough_portal',
    ],
    super_admin: [
        // Everything Admin has, plus financials + user management + delete.
        'view_clients', 'edit_clients',
        'view_estimates', 'edit_estimates', 'view_estimate_pricing', 'edit_pricing', 'assign_estimates',
        'view_dispatch', 'manage_dispatch', 'manage_crew', 'manage_crew_logins',
        'view_records', 'send_invoices', 'delete_records',
        'manage_settings', 'manage_templates', 'manage_users', 'view_financials', 'transfer_deposits',
        'use_walkthrough_portal',
    ],
    owner: ALL, // wildcard — sees everything, present + future
};

// ---- Display config for the Users & Roles UI -----------------------------
// Order = the order roles appear in the dropdown (top = least-privileged).
// `hidden` means the role exists in the DB but isn't shown as a selectable
// option — owners can only be managed via SQL.
export const ROLE_DISPLAY = [
    { key: 'office',      label: 'Office',      description: 'Default access. View clients, build estimates, see dispatch.' },
    { key: 'admin',       label: 'Admin',       description: 'Office lead. Full operational control + settings + invoicing.' },
    { key: 'super_admin', label: 'Super Admin', description: 'Financial visibility + user management. The three brothers.' },
    { key: 'owner',       label: 'Owner',       description: 'Total control. Hidden role — only set via database.', hidden: true },
];

// ---- canFor() — the core authorization predicate -------------------------
// Used by useCan() to compute the actual answer for a (role, overrides, cap)
// tuple. Extracted as a pure function so it's testable and also usable
// outside of React contexts (e.g. server-side route guards if we add those).
export function canFor(role, overrides, capability) {
    if (!role) return false;
    if (!CAPABILITY_NAMES.has(capability)) return false; // typo guard
    const roleCaps = ROLE_CAPABILITIES[role];
    if (roleCaps === ALL) return true;
    if (Array.isArray(roleCaps) && roleCaps.includes(capability)) return true;
    if (Array.isArray(overrides) && overrides.includes(capability)) return true;
    return false;
}

// Convenience: get every capability a user effectively has, role + overrides
// merged + de-duped. Used by the Users & Roles UI to know which checkboxes
// should appear pre-checked-and-disabled (granted by role) vs. checkable
// (additional override).
export function effectiveCapabilities(role, overrides) {
    const roleCaps = ROLE_CAPABILITIES[role];
    if (roleCaps === ALL) return CAPABILITIES.map(c => c.name);
    const set = new Set();
    if (Array.isArray(roleCaps)) roleCaps.forEach(c => set.add(c));
    if (Array.isArray(overrides)) overrides.forEach(c => set.add(c));
    return [...set];
}

// Convenience: list the capabilities granted by the role specifically (NOT
// including overrides). Used to gray-out + lock checkboxes that the user
// has "for free" via their role tier.
export function roleDefaultCapabilities(role) {
    const roleCaps = ROLE_CAPABILITIES[role];
    if (roleCaps === ALL) return CAPABILITIES.map(c => c.name);
    return Array.isArray(roleCaps) ? roleCaps.slice() : [];
}
